import { SEED } from '@/data';

export type C3Row = Record<string, unknown>;
type Component3Data = Record<string, unknown> & {
  firm?: string;
  ref?: string;
  contractValue?: number | string;
  remuneration?: number | string;
  reimbursable?: number | string;
  nonCompetitive?: number | string;
  deliverables?: C3Row[];
  deliverableDetails?: C3Row[];
  expenses?: C3Row[];
  expenseQuarters?: unknown[];
  contingencies?: C3Row[];
  provisionalSumTotal?: number | string;
  contingencyTotal?: number | string;
  provisionalSums?: C3Row[];
};

interface C3Month {
  key: string;
  label?: string;
}

export function formatC3Amt(n: unknown): string {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!v && v !== 0) return '—';
  return v.toLocaleString('en-PK');
}

export function formatPkr(n: unknown): string {
  const v = Number(n) || 0;
  if (v >= 1e7) return `PKR ${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `PKR ${(v / 1e5).toFixed(2)} L`;
  return `PKR ${v.toLocaleString('en-PK')}`;
}

export function formatC3MonthCell(v: unknown): string {
  if (v == null || v === '' || Number(v) === 0) return '—';
  return Number(v).toLocaleString('en-PK');
}

export function c3DeliverableStats(d: Component3Data) {
  const rows = (d.deliverables || []);
  const totNet = rows.reduce((a, r) => a + (Number(r.net) || 0), 0);
  const totPra = rows.reduce((a, r) => a + (Number(r.pra) || 0), 0);
  const totGross = rows.reduce((a, r) => a + (Number(r.gross) || 0), 0);
  const recvGross = rows.reduce((a, r) => a + (Number(r.received) || 0), 0);
  const outGross = rows.reduce((a, r) => a + (Number(r.outstanding) || 0), 0);
  const paidNet = rows.filter((r) => r.status === 'Paid').reduce((a, r) => a + (Number(r.net) || 0), 0);
  const outNet = totNet - paidNet;
  const paidPct = totNet ? Math.round((paidNet / totNet) * 100) : 0;
  const outPct = 100 - paidPct;
  return { rows, totNet, totPra, totGross, recvGross, outGross, paidNet, outNet, paidPct, outPct };
}

export function c3ExpenseStats(rows: C3Row[], visibleMonths: C3Month[]) {
  const paidSum = rows.reduce((a, e) => a + (Number(e.paid) || 0), 0);
  const remSum = rows.reduce((a, e) => a + (Number(e.remaining) || 0), 0);
  const totSum = rows.reduce((a, e) => a + (Number(e.totalAmount) || 0), 0);
  const monthTotals: Record<string, number> = {};
  visibleMonths.forEach((m) => {
    monthTotals[m.key] = rows.reduce((a, e) => {
      const months = (e.months || {}) as Record<string, unknown>;
      return a + (Number(months[m.key]) || 0);
    }, 0);
  });
  const pct = totSum ? Math.round((paidSum / totSum) * 100) : 0;
  return { paidSum, remSum, totSum, monthTotals, pct, count: rows.length };
}

export function getC3VisibleMonths(quarter: string): C3Month[] {
  const map: Record<string, string[] | null> = {
    '': null,
    q1: ['mar25', 'apr25', 'may25'],
    q2: ['jun25', 'jul25', 'aug25'],
    q3: ['sep25', 'oct25', 'nov25'],
    q4: ['dec25', 'jan26', 'feb26'],
  };
  const keys = map[quarter];
  if (!keys) return SEED.COMPONENT3_MONTHS;
  return (SEED.COMPONENT3_MONTHS as C3Month[]).filter((m) => keys.includes(m.key));
}

export function filterC3DeliverableDetails(rows: C3Row[], statusFilter = 'all'): C3Row[] {
  const list = rows || [];
  if (statusFilter === 'paid') {
    return list.filter((r) => String(r.status || '').toLowerCase() === 'paid');
  }
  if (statusFilter === 'outstanding') {
    return list.filter((r) => String(r.status || '').toLowerCase() === 'outstanding');
  }
  return list;
}

export function filterC3DeliverablePayments(rows: C3Row[], statusFilter = 'all'): C3Row[] {
  const list = rows || [];
  if (statusFilter === 'paid') {
    return list.filter((r) => r.status === 'Paid');
  }
  if (statusFilter === 'outstanding') {
    return list.filter((r) => r.status === 'Outstanding');
  }
  return list;
}

export function c3DeliverableDetailCounts(rows: C3Row[]) {
  const list = rows || [];
  return {
    total: list.length,
    paid: list.filter((r) => String(r.status || '').toLowerCase() === 'paid').length,
    outstanding: list.filter((r) => String(r.status || '').toLowerCase() === 'outstanding').length,
  };
}

export function c3ExpenseHeadOptions(expenses: C3Row[]): string[] {
  return (expenses || [])
    .map((e) => String(e.head || ''))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

export function getC3MonthDate(monthKey: string): Date | null {
  const map: Record<string, [number, number]> = {
    mar25: [2025, 3], apr25: [2025, 4], may25: [2025, 5],
    jun25: [2025, 6], jul25: [2025, 7], aug25: [2025, 8],
    sep25: [2025, 9], oct25: [2025, 10], nov25: [2025, 11],
    dec25: [2025, 12], jan26: [2026, 1], feb26: [2026, 2],
  };
  const hit = map[monthKey];
  return hit ? new Date(hit[0], hit[1] - 1, 1) : null;
}

export function isC3MonthLocked(monthKey: string, asOf: Date = new Date()): boolean {
  const monthDate = getC3MonthDate(monthKey);
  if (!monthDate) return false;
  const current = new Date(asOf.getFullYear(), asOf.getMonth(), 1);
  return monthDate < current;
}

export function isC3ExpenseMonthValueLocked(existingValue: unknown): boolean {
  if (existingValue == null || existingValue === '') return false;
  const n = Number(existingValue);
  return !Number.isNaN(n) && n > 0;
}

export function getC3VisibleMonthsForSelection(monthKey: string): C3Month[] {
  if (!monthKey) return SEED.COMPONENT3_MONTHS;
  return (SEED.COMPONENT3_MONTHS as C3Month[]).filter((m) => m.key === monthKey);
}

export interface C3ExpenseFilters {
  search?: string;
  head?: string;
  pay?: string;
}

export function filterC3Expenses(expenses: C3Row[], { search = '', head = '', pay = '' }: C3ExpenseFilters = {}) {
  const q = search.toLowerCase();
  return (expenses || []).filter((e) => {
    const text = String(e.head || '').toLowerCase();
    if (head && e.head !== head) return false;
    if (q && !text.includes(q)) return false;
    const paid = Number(e.paid) || 0;
    const rem = Number(e.remaining) || 0;
    if (pay === 'unpaid' && paid !== 0) return false;
    if (pay === 'partial' && !(paid > 0 && rem > 0)) return false;
    if (pay === 'paid' && !(paid > 0 && rem === 0)) return false;
    return true;
  });
}

export function c3StatusBadge(status: unknown): string {
  if (status === 'Paid' || status === 'Completed') return 'Completed';
  if (status === 'Outstanding' || status === 'Pending') return 'Pending';
  return String(status || '—');
}

export function c3DeliverableDetailStatusLabel(status: unknown): string {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'Approved';
  if (s === 'outstanding') return 'Pending/Review In Process';
  return String(status || '—');
}

export function isC3DeliverableLocked(row: C3Row): boolean {
  return String(row?.status || '').toLowerCase() === 'paid';
}

export function isC3PaymentLocked(row: C3Row): boolean {
  return row?.status === 'Paid';
}

export function isValidComponent3(d: unknown): d is Component3Data {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return false;
  const data = d as Component3Data;
  return !!(
    data.firm
    && Array.isArray(data.deliverableDetails)
    && data.deliverableDetails.length >= 12
    && Array.isArray(data.deliverables)
    && data.deliverables.length >= 12
    && Array.isArray(data.expenses)
    && data.expenses.length >= 7
  );
}

export function c3RecalcDeliverable(r: C3Row): C3Row {
  const net = Number(r.net) || 0;
  r.net = net;
  r.pra = Math.round(net * 0.05);
  r.gross = Number(r.net) + Number(r.pra);
  const recvRaw = r.received;
  const recv = recvRaw != null && recvRaw !== '' ? Number(recvRaw) : null;
  if (recv != null && !Number.isNaN(recv)) {
    r.received = recv;
    r.outstanding = Math.max(0, Number(r.gross) - recv);
  } else {
    r.received = null;
    r.outstanding = r.status === 'Outstanding' ? r.gross : (r.status === 'Paid' ? 0 : null);
  }
  return r;
}

export function c3RecalcExpense(e: C3Row): C3Row {
  e.months = (e.months || {});
  const months = e.months as Record<string, unknown>;
  const paid = (SEED.COMPONENT3_MONTHS as C3Month[]).reduce(
    (a, m) => a + (Number(months[m.key]) || 0),
    0,
  );
  e.qty = Number(e.qty) || 0;
  const storedTotal = Number(e.totalAmount) || 0;
  e.totalAmount = storedTotal || paid;
  e.ceiling = Number(e.qty) ? Math.round(Number(e.totalAmount) / Number(e.qty)) : 0;
  e.paid = paid;
  e.remaining = Math.max(0, Number(e.totalAmount) - paid);
  return e;
}

export function formatC3CompactM(n: number) {
  const v = Number(n) || 0;
  if (v >= 1e6) return `PKR ${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `PKR ${(v / 1e3).toFixed(0)}K`;
  return `PKR ${Math.round(v).toLocaleString('en-PK')}`;
}

export function formatC3UsedRemainingPct(used: number, total: number): string {
  if (!total) return '—';
  const usedPct = Math.round((used / total) * 1000) / 10;
  const remPct = Math.round((100 - usedPct) * 10) / 10;
  return `${usedPct}% used · ${remPct}% remaining`;
}

export function c3ProvisionalSumStats(d: Component3Data) {
  const totalAmount = Number(d.provisionalSumTotal) || 0;
  const rows = d.provisionalSums || [];
  let used = 0;
  rows.forEach((r) => {
    if (String(r.status || '') === 'Paid') used += Number(r.invoiceValue) || 0;
  });
  const remaining = Math.max(0, totalAmount - used);
  const pct = totalAmount ? Math.round((used / totalAmount) * 1000) / 10 : 0;
  return {
    totalAmount,
    used,
    remaining,
    pct,
    count: rows.length,
    paidCount: rows.filter((r) => String(r.status) === 'Paid').length,
    unpaidCount: rows.filter((r) => String(r.status) !== 'Paid').length,
  };
}

export function projectC3ProvisionalSumStats(
  d: Component3Data,
  editIndex: number | null | undefined,
  draftValue: number,
  draftStatus: string,
) {
  const totalAmount = Number(d.provisionalSumTotal) || 0;
  const rows = (d.provisionalSums || []).filter((_, i) => i !== editIndex);
  let used = 0;
  rows.forEach((r) => {
    if (String(r.status || '') === 'Paid') used += Number(r.invoiceValue) || 0;
  });
  const val = Number(draftValue) || 0;
  if (val > 0 && String(draftStatus) === 'Paid') used += val;
  const remaining = Math.max(0, totalAmount - used);
  const pct = totalAmount ? Math.round((used / totalAmount) * 1000) / 10 : 0;
  return { totalAmount, used, remaining, pct, count: rows.length + 1 };
}

export function c3ContingencyStats(d: Component3Data) {
  const totalAmount = Number(d.contingencyTotal) || Number(d.nonCompetitive) || 0;
  const rows = d.contingencies || [];
  let used = 0;
  let unpaid = 0;
  rows.forEach((r) => {
    const amt = Number(r.invoiceValue ?? r.amount) || 0;
    const status = String(r.status || 'Unpaid');
    if (status === 'Paid' || status === 'Disbursed') used += amt;
    else unpaid += amt;
  });
  const remaining = Math.max(0, totalAmount - used);
  const pct = totalAmount ? Math.round((used / totalAmount) * 1000) / 10 : 0;
  return { totalAmount, used, disbursed: used, pending: unpaid, remaining, pct, count: rows.length };
}

/** Live preview for add/edit contingency modal (replaces row at editIndex with draft). */
export function projectC3ContingencyStats(
  d: Component3Data,
  editIndex: number | null | undefined,
  draftAmount: number,
  draftStatus: string,
) {
  const totalAmount = Number(d.contingencyTotal) || Number(d.nonCompetitive) || 0;
  const rows = (d.contingencies || []).filter((_, i) => i !== editIndex);
  let used = 0;
  let pending = 0;
  rows.forEach((r) => {
    const amt = Number(r.invoiceValue ?? r.amount) || 0;
    const status = String(r.status || 'Unpaid');
    if (status === 'Paid' || status === 'Disbursed') used += amt;
    else pending += amt;
  });
  const amt = Number(draftAmount) || 0;
  const status = String(draftStatus || 'Unpaid');
  if (amt > 0) {
    if (status === 'Paid' || status === 'Disbursed') used += amt;
    else pending += amt;
  }
  const remaining = Math.max(0, totalAmount - used);
  const pct = totalAmount ? Math.round((used / totalAmount) * 1000) / 10 : 0;
  return {
    totalAmount,
    used,
    disbursed: used,
    pending,
    remaining,
    pct,
    count: rows.length + 1,
  };
}

export function c3RowAllocPct(alloc: number, invoiceValue: number): string {
  if (!alloc) return '—';
  const usedPct = Math.round((invoiceValue / alloc) * 1000) / 10;
  const remPct = Math.round((100 - usedPct) * 10) / 10;
  return `${usedPct}% used · ${remPct}% remaining`;
}

export function mergeComponent3(saved: unknown): Component3Data {
  const seed = JSON.parse(JSON.stringify(SEED.COMPONENT3_CS06)) as Component3Data;
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return seed;
  const data = saved as Component3Data;
  return {
    ...seed,
    ...data,
    ref: data.ref || seed.ref,
    firm: data.firm || seed.firm,
    contractValue: data.contractValue ?? seed.contractValue,
    remuneration: data.remuneration ?? seed.remuneration,
    reimbursable: data.reimbursable ?? seed.reimbursable,
    nonCompetitive: data.nonCompetitive ?? seed.nonCompetitive,
    provisionalSumTotal: data.provisionalSumTotal ?? seed.provisionalSumTotal,
    contingencyTotal: data.contingencyTotal ?? seed.contingencyTotal,
    deliverables: data.deliverables?.length ? data.deliverables : seed.deliverables,
    deliverableDetails: data.deliverableDetails?.length ? data.deliverableDetails : seed.deliverableDetails,
    expenses: data.expenses?.length ? data.expenses : seed.expenses,
    expenseQuarters: data.expenseQuarters?.length ? data.expenseQuarters : seed.expenseQuarters,
    provisionalSums: Array.isArray(data.provisionalSums) ? data.provisionalSums : (seed.provisionalSums || []),
    contingencies: Array.isArray(data.contingencies) ? data.contingencies : (seed.contingencies || []),
  };
}
